import SEO from '@/components/SEO';
import { Link } from 'react-router-dom';

const reviewItems = [
  {
    title: 'Profile Verification',
    body: 'Artist and collaborator profiles may be reviewed by the Kala Manch admin team before trust badges or public visibility are granted.',
  },
  {
    title: 'Media Review',
    body: 'Profile images, portfolio links, and performance videos can be removed if they are misleading, unsafe, adult, hateful, abusive, or not relevant to creative work.',
  },
  {
    title: 'Private Message Board',
    body: 'PMB is meant for genuine project, event, collaboration, and hiring conversations. Spam, harassment, and unsafe requests are not allowed.',
  },
  {
    title: 'Projects, Competitions, and Auditions',
    body: 'Public work opportunities, competitions, and live auditions are reviewed so artists and collaborators are not exposed to unsafe, fake, adult, abusive, or misleading listings.',
  },
  {
    title: 'Workshops, Classes, and Enrollments',
    body: 'Workshop, class, course, training, and enrollment listings or conversations must be truthful, lawful, and safe. Participants should verify organizer identity, fees, timing, venue or online link, cancellation terms, and suitability before joining.',
  },
  {
    title: 'AI Studio Safety',
    body: 'Unsafe AI Studio prompts and outputs may be blocked, logged, reviewed, and used to restrict accounts that try to generate harmful or illegal content.',
  },
];

const prohibitedItems = [
  'Adult, explicit, pornographic, or sexually suggestive media.',
  'Abusive, threatening, hateful, discriminatory, or harassing content.',
  'Fake identity, impersonation, misleading portfolio claims, or copied work presented as original.',
  'Spam, scam offers, payment fraud, or requests that are not related to creative work.',
  'Requests for advance payments, refunds, commissions, deposits, or financial transfers that appear fraudulent, coercive, misleading, or outside a genuine creative arrangement.',
  'Unsafe auditions, casting calls, or project listings that request exploitative, adult, illegal, or private activity.',
  'AI prompts or generated content intended for abuse, impersonation, sexual content, hate, scams, or illegal activity.',
  'Content that violates copyright, privacy, or applicable law.',
];

const TrustSafety = () => {
  return (
    <main className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-purple-50 pt-24 sm:pt-32">
      <SEO
        title="Trust & Safety"
        description="Read Kalamanch.art trust and safety rules for profiles, media, private messages, artist verification, collaborator accounts, and content moderation."
        keywords="Kalamanch trust safety, artist verification, content rules, profile moderation, media review"
      />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,111,0,0.16),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(74,20,140,0.16),transparent_30%)]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-18">
          <p className="uppercase tracking-[0.35em] text-sm font-semibold text-[#FF6F00] mb-5">Trust & Safety</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight mb-5">
            A safer creative stage for artists and collaborators.
          </h1>
          <p className="text-lg text-gray-700 max-w-3xl leading-relaxed">
            Kalamanch.art, owned and operated by Tivaro LLP, is built for genuine creative discovery, respectful
            conversations, and real opportunities. These rules explain what we review, what is not allowed, and how
            content can be reported.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {reviewItems.map((item) => (
            <article key={item.title} className="rounded-[2rem] bg-white p-6 shadow-lg ring-1 ring-orange-100">
              <h2 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h2>
              <p className="text-gray-600 leading-relaxed">{item.body}</p>
            </article>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1fr_0.8fr] gap-8">
          <article className="rounded-[2rem] bg-white p-6 sm:p-8 shadow-lg ring-1 ring-orange-100">
            <h2 className="text-3xl font-bold gradient-text mb-5">Not Allowed</h2>
            <ul className="space-y-4 text-gray-700">
              {prohibitedItems.map((item) => (
                <li key={item} className="flex gap-3">
                  <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-100 text-xs font-bold text-red-600">!</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </article>

          <aside className="rounded-[2rem] bg-gradient-to-br from-[#4A148C] to-[#FF6F00] p-6 sm:p-8 text-white shadow-xl">
            <h2 className="text-3xl font-bold mb-4">Report a Concern</h2>
            <p className="text-white/90 leading-relaxed mb-6">
              If you see a profile, image, video, or message that looks unsafe or misleading, contact the Kala Manch
              team with the profile link and a short explanation.
            </p>
            <Link to="/contact?subject=Trust%20%26%20Safety%20Concern" className="inline-flex w-full justify-center rounded-full bg-white px-5 py-3 font-semibold text-[#4A148C] transition hover:bg-orange-50">
              Report Through Contact Form
            </Link>
          </aside>
        </div>

        <div className="mt-8 rounded-[2rem] bg-white p-6 sm:p-8 shadow-lg ring-1 ring-orange-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Admin Moderation</h2>
          <p className="text-gray-700 leading-relaxed">
            The admin panel may be used to verify profiles, feature artists, review media, remove unsafe images or
            videos, inspect PMB thread counts and messages for safety, review projects, competitions, auditions,
            AI Studio safety alerts, and delete content that violates platform rules.
            Moderation decisions are made to protect artists, collaborators, visitors, and the reputation of the Kala
            Manch platform.
          </p>
        </div>

        <div className="mt-8 rounded-[2rem] bg-white p-6 sm:p-8 shadow-lg ring-1 ring-orange-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Platform Responsibility</h2>
          <p className="text-gray-700 leading-relaxed">
            Tivaro LLP owns and operates Kalamanch.art and maintains these trust and safety standards for the
            platform, its website, mobile apps, public profiles, collaboration tools, and support channels. Independent
            collaborations, classes, workshops, bookings, contracts, payments, refunds, meetings, travel, and offline
            dealings remain between the concerned users. Kalamanch.art is not a party to those dealings unless a
            specific official platform feature expressly states otherwise.
          </p>
        </div>
      </section>
    </main>
  );
};

export default TrustSafety;
